import React, { useEffect, useState } from "react";
import { UserCheck, Mail, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { ccBtnPink, ccBtnBlue, EmptyState } from "../care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HireCaregivers() {
  const [list, setList] = useState(null);

  const load = () =>
    fetch(`${API}/api/workspace/caregivers-for-hire`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setList)
      .catch((err) => console.error(err));

  useEffect(() => {
    load();
  }, []);

  const hire = async (email) => {
    try {
      const res = await fetch(`${API}/api/workspace/caregivers-for-hire/${encodeURIComponent(email)}/hire`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Hire failed");
      toast.success("Caregiver marked as hired — they'll see your facility in their workspace.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="hire-caregivers">
      <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2 mb-1">
        <HeartHandshake className="w-4 h-4 text-[#E5A0BC]" /> Caregivers Open to Facility Hire
      </h4>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
        Independent caregivers who opted in from their dashboard. Contact them directly or mark them hired to link them to your facility.
      </p>
      {list === null ? (
        <p className="text-sm text-[#9CA3AF] font-semibold">Loading caregivers…</p>
      ) : list.length === 0 ? (
        <EmptyState text="No caregivers are listed for hire right now. Invite your staff to publish Care Connections profiles!" testId="hire-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((c) => (
            <div key={c.email} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 dark:bg-[#273244] dark:border-[#374457]" data-testid={`hire-card-${c.email}`}>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#FCE6E9] flex items-center justify-center text-sm font-black text-[#E5A0BC] font-heading">
                  {(c.name || c.email).charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-black text-[#374151] dark:text-slate-100 truncate">{c.name || c.email}</p>
                  <p className="text-[11px] text-[#9CA3AF] font-medium truncate">{c.headline || "Caregiver"}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <a href={`mailto:${c.email}`} className={ccBtnBlue} data-testid={`hire-contact-${c.email}`}>
                  <Mail className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Contact
                </a>
                {c.hired ? (
                  <span className="px-4 py-2 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold" data-testid={`hire-hired-${c.email}`}>
                    Hired ✓
                  </span>
                ) : (
                  <button onClick={() => hire(c.email)} className={ccBtnPink} data-testid={`hire-btn-${c.email}`}>
                    <UserCheck className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Mark Hired
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
